// src/lib/menage-photo-commit.js — ADR-208
//
// Commit d'une photo uploadée vers `menage_runs.photos` (append au JSONB).
// MUTUALISÉ entre :
//   - MenageTodayView (drain file photo, rôle ménage)
//   - AdminChecklistEditor (drain file photo en contexte admin, ResaV2Modal)
// pour éviter la divergence (un seul format d'entrée, une seule logique d'append).
//
// `item` vient de la file d'upload (use-photo-upload-queue) : { runId, pieceId,
// kind, path, publicUrl }. Pour kind='piece' (bucket PRIVÉ menage-photos) on
// stocke le `path` (signé à l'affichage) ; sinon l'`url` publique (incident,
// bucket public — jusqu'au Lot 2 photo). RLS : menage_update_own (≤48h) OU
// admin_all autorise l'UPDATE selon le rôle de la session.

//
// ⚠️ LOT « TRAVAIL PERDU » — deux gardes, chacune née d'une perte réelle :
//   1. Lecture en échec ⇒ on n'écrit RIEN et on lève. Avant, l'erreur était
//      ignorée : `current` valait null, la liste réécrite ne contenait que la
//      nouvelle photo, et toutes les preuves déjà enregistrées disparaissaient.
//   2. Écriture contrôlée par `.select('id')` + nombre de lignes. Une écriture
//      bloquée par le verrou 48 h (RLS) rend 0 ligne SANS erreur : sans ce
//      contrôle, la file croyait la photo enregistrée et la jetait.
// Une erreur levée ici laisse la photo dans la file (use-photo-upload-queue),
// qui retentera.
//
// RESTE (demande une migration, non faite ici) : lire puis réécrire n'est pas
// atomique ; deux écritures simultanées sur le même ménage pourraient encore
// s'écraser. Le remède propre est une fonction base qui ajoute sans relire
// (`photos = photos || entry`).

// Erreur DÉFINITIVE : retenter ne changera rien (0 ligne = verrou 48 h ou
// ménage réaffecté, invisible pour cet agent ; ménage introuvable). La file
// (use-photo-upload-queue) l'arrête et alerte l'admin UNE fois. Les erreurs
// de lecture/écriture renvoyées par la base (réseau…) restent passagères.
function definitif(message) {
  const e = new Error(message);
  e.definitif = true;
  return e;
}

export async function commitUploadedPhoto(supabase, item) {
  const { data: current, error: readError } = await supabase
    .from("menage_runs")
    .select("photos")
    .eq("id", item.runId)
    .maybeSingle();
  if (readError) {
    throw new Error(
      `Lecture des photos du ménage impossible : ${readError.message || readError}`,
    );
  }
  if (!current) {
    throw definitif("Ménage introuvable : photo non enregistrée");
  }
  const entry =
    item.kind === "piece"
      ? {
          piece_id: item.pieceId,
          path: item.path,
          uploaded_at: new Date().toISOString(),
        }
      : {
          piece_id: item.pieceId,
          url: item.publicUrl,
          uploaded_at: new Date().toISOString(),
        };
  const before = Array.isArray(current.photos) ? current.photos : [];
  // Rejeu sans doublon : la file retente une photo dont l'enregistrement a
  // échoué. Si la base l'avait en fait reçue (réponse perdue), son chemin est
  // déjà là — on ne l'ajoute pas une seconde fois.
  if (
    item.kind === "piece" &&
    item.path &&
    before.some((p) => p && p.path === item.path)
  ) {
    return before;
  }
  const next = [...before, entry];
  const { data: rows, error: writeError } = await supabase
    .from("menage_runs")
    .update({ photos: next })
    .eq("id", item.runId)
    .select("id");
  if (writeError) {
    throw new Error(
      `Enregistrement de la photo refusé : ${writeError.message || writeError}`,
    );
  }
  if (!Array.isArray(rows) || rows.length === 0) {
    throw definitif(
      "Enregistrement de la photo refusé : aucune ligne modifiée (ménage verrouillé ?)",
    );
  }
  return next;
}
